import { useRef, useState } from "react";
import { RouteFactory } from "@app/routePaths";
import { NewsletterArchiveKeywordsEnums } from "@app/types/enums";
import { NewsletterArchiveContext } from "@app/types/Page";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import {
  THRIVE_CONTRIBUTORS,
  THRIVE_DESC,
  THRIVE_TITLE,
} from "@components/Newsletter/constants";
import HomepageCarouselNewsletter from "@components/Newsletter/HomepageCarouselNewsletter";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import NewsletterArchiveArticlesList from "@pages/Newsletter/Archive/components/NewsletterArchiveArticlesList";
import NewsletterArchiveHeader from "@pages/Newsletter/Archive/components/NewsletterArchiveHeader";
import SocialShare from "@pages/Newsletter/Archive/components/SocialShare";
import ThriveSampleIssue from "@pages/Newsletter/Archive/Thrive/SampleIssues/ThriveSampleIssue";
import { ENVIRONMENT } from "@util/constant";
import { cn, getEnvironmentDomain } from "@util/helpers";
import { useDetectAdBlock } from "adblock-detect-react";

type ThriveArchiveContentProps = Pick<NewsletterArchiveContext, "data">;

export default function ThriveArchivePageContent({
  data: { title, overview, seoFooterData },
}: ThriveArchiveContentProps): React.ReactElement {
  const adBlockDetected = useDetectAdBlock();

  const divRef = useRef<HTMLDivElement>(null);
  const [isKeepReadingVisible, setIsKeepReadingVisible] = useState(true);

  const thriveNL = BT_NEWSLETTERS.filter(
    ({ type }) => type === NEWSLETTER_TYPE.THRIVE
  );

  const handleButtonClick = () => {
    setIsKeepReadingVisible(false);
  };

  const dynamicClasses = !isKeepReadingVisible ? "overflow-visible h-full" : "";

  const baseUrl = getEnvironmentDomain(ENVIRONMENT);

  return (
    <div className="w-full" data-testid="thrive-archive-page-content-component">
      <AdSettings
        adNames={["lb1", "imu1", "catfish"]}
        path={RouteFactory.newsletterThriveArchivePage}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <MetaTags
        title={title}
        description="Read more at The Business Times."
        slug={RouteFactory.newsletterThriveArchivePage}
      />

      <GAData
        title="thrive"
        level2="newsletter"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <Container rootClassName="pt-5 !px-5">
        <div
          className="border-b-2 border-black"
          data-testid="thrive-archive-page-content"
        >
          <NewsletterArchiveHeader
            newsletterTitle={THRIVE_TITLE}
            newsletterDescription={THRIVE_DESC}
            newsletterContributors={THRIVE_CONTRIBUTORS}
            newsletterRoute={RouteFactory.newsletterThriveArchivePage}
            newsletterTime={"Friday, 3 pm"}
            signUpNL={thriveNL[0]}
            divRef={divRef}
            displayNewsletterForm={true}
          />
        </div>

        <NewsletterArchiveArticlesList
          newsletterKeyword={NewsletterArchiveKeywordsEnums.Thrive}
          articles={overview}
        />

        <div className="relative flex flex-col lg:hidden">
          <ThriveSampleIssue
            divRef={divRef}
            firstChildClassName={dynamicClasses}
            className={cn("h-[600px]", dynamicClasses)}
          />

          {isKeepReadingVisible ? (
            <div className="absolute bottom-0 mb-[1px] ml-4 w-11/12 bg-nlArchiveGradient px-4 pb-4 pt-20">
              <button
                className="flex w-full flex-row
                items-start justify-center rounded-3xl border border-gray-850 py-3 lg:hidden"
                onClick={handleButtonClick}
              >
                KEEP READING
              </button>
            </div>
          ) : null}
        </div>

        <SocialShare
          shareUrl={baseUrl + RouteFactory.newsletterThriveArchivePage}
          rootClassName={"pt-12 block lg:hidden pb-20"}
        />

        <HomepageCarouselNewsletter
          newsletterToSkip={NEWSLETTER_TYPE.THRIVE}
          showSeeMore={false}
          blockTitle="YOU MAY LIKE "
          rootClassName="border-t border-black pt-3 mb-16"
        />
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
}
